import { useState, useMemo, useCallback } from "react";
import { Button, addToast } from "@heroui/react";
import { FolderSimpleIcon, ArrowRightIcon } from "@phosphor-icons/react";
import { useShallow } from "zustand/shallow";

import { useBoundStore } from "@/app/store";
import { FolderTreeNode } from "@/app/store/foldersSlice";
import { NestedContentNavigator, NestedAction } from "@/shared/components/ui/NestedContentNavigator";
import { useOpenState } from "@/shared/hooks/useOpenState";
import clsx from "clsx";
import { getFolderColorClassName } from "@/features/folders/utils/general";

interface BulkMoveNotesToFolderOptionProps {
  noteIds: Set<number>;
  folderIds?: Set<number>;
  currentFolderId?: number | null;
  onClearSelection?: () => void;
}

const ROOT_FOLDER_NAME = "Home";
export const BULK_MOVE_NOTES_DRAWER_ID = "bulk-move-notes";

export function BulkMoveNotesToFolderOption({
  noteIds,
  folderIds = new Set(),
  currentFolderId,
  onClearSelection,
}: BulkMoveNotesToFolderOptionProps) {
  const folders = useBoundStore(useShallow((state) => state.folders.main));
  const folderTree = useBoundStore(useShallow((state) => state.folders.tree));
  const notes = useBoundStore(useShallow((state) => state.notes.main));
  const updateNote = useBoundStore(useShallow((state) => state.notes.updateNote));
  const updateFolder = useBoundStore(useShallow((state) => state.folders.updateFolder));
  const [isMoving, setIsMoving] = useState(false);

  // Use the drawer state to close it programmatically
  const { handleOpenChange } = useOpenState({ id: BULK_MOVE_NOTES_DRAWER_ID });

  const handleMoveItems = useCallback(
    async (targetFolderId: number | null) => {
      if (targetFolderId === currentFolderId) {
        handleOpenChange(false);
        return;
      }

      const noteCount = noteIds.size;
      const folderCount = folderIds.size;
      const totalCount = noteCount + folderCount;

      if (totalCount === 0) {
        handleOpenChange(false);
        return;
      }

      setIsMoving(true);

      const movePromises: Promise<unknown>[] = [];

      // Move notes
      if (noteCount > 0) {
        const noteMovePromises = Array.from(noteIds).map((noteId) => {
          const note = notes.find((n) => n.id === noteId);
          const isShared = Boolean(note?.public_uuid);
          return updateNote(noteId, { folder_id: targetFolderId }, isShared);
        });
        movePromises.push(...noteMovePromises);
      }

      // Move folders
      if (folderCount > 0) {
        const folderMovePromises = Array.from(folderIds).map((folderId) => {
          // Prevent moving a folder into itself or into one of its descendants
          if (targetFolderId !== null) {
            const targetFolder = folders.find((f) => f.id === targetFolderId);
            const movingFolder = folders.find((f) => f.id === folderId);

            if (targetFolder && movingFolder) {
              // Check if target folder is a descendant of the folder being moved
              const isDescendant = (parentId: number, checkId: number): boolean => {
                const children = folders.filter((f) => f.parent_id === parentId);
                return children.some((child) => child.id === checkId || isDescendant(child.id, checkId));
              };

              if (folderId === targetFolderId || isDescendant(folderId, targetFolderId)) {
                // Skip this folder
                return Promise.resolve();
              }
            }
          }

          return updateFolder(folderId, { parent_id: targetFolderId });
        });
        movePromises.push(...folderMovePromises);
      }

      const targetFolderName = targetFolderId ? folders.find((f) => f.id === targetFolderId)?.name : ROOT_FOLDER_NAME;

      Promise.all(movePromises)
        .then(() => {
          let message = "";
          if (noteCount > 0 && folderCount > 0) {
            message = `${noteCount} note${noteCount > 1 ? "s" : ""} and ${folderCount} folder${folderCount > 1 ? "s" : ""} moved to ${targetFolderName}`;
          } else if (noteCount > 0) {
            message = `${noteCount} note${noteCount > 1 ? "s" : ""} moved to ${targetFolderName}`;
          } else {
            message = `${folderCount} folder${folderCount > 1 ? "s" : ""} moved to ${targetFolderName}`;
          }

          addToast({
            title: message,
          });
        })
        .catch((error) => {
          console.error("Failed to move items:", error);
          addToast({
            title: "Failed to move items",
            color: "danger",
          });
        });

      handleOpenChange(false);
      onClearSelection?.();
      setIsMoving(false);
    },
    [noteIds, folderIds, currentFolderId, handleOpenChange, updateNote, updateFolder, folders, notes, onClearSelection]
  );

  // Build folder actions for any given set of folders
  const buildFolderActions = useCallback(
    (folderNodes: FolderTreeNode[]): NestedAction<string>[] => {
      return folderNodes
        .filter((folder) => !folderIds.has(folder.id)) // Don't show folders that are being moved
        .map((folder) => {
          const hasChildren = folder.children.length > 0;
          const isCurrentFolder = currentFolderId === folder.id;

          if (hasChildren) {
            // For folders with children, create a nested structure with "Move here" and children
            return {
              key: folder.id.toString(),
              label: folder.name,
              icon: (
                <FolderSimpleIcon
                  className={clsx("shrink-0", getFolderColorClassName(folder.color))}
                  size={18}
                  weight="duotone"
                />
              ),
              nestedActions: () => [
                // "Move here" action for the folder itself, disabled if current
                {
                  key: `${folder.id}-move`,
                  label: `Move to "${folder.name}"`,
                  icon: (
                    <FolderSimpleIcon
                      className={clsx("shrink-0", getFolderColorClassName(folder.color))}
                      size={18}
                      weight="duotone"
                    />
                  ),
                  run: isCurrentFolder ? undefined : () => handleMoveItems(folder.id),
                },
                // Children folders
                ...buildFolderActions(folder.children),
              ],
            };
          } else {
            // For folders without children, show as direct action
            return {
              key: folder.id.toString(),
              label: folder.name,
              icon: (
                <FolderSimpleIcon
                  className={clsx("shrink-0", getFolderColorClassName(folder.color))}
                  size={18}
                  weight="duotone"
                />
              ),
              run: isCurrentFolder ? undefined : () => handleMoveItems(folder.id),
            };
          }
        });
    },
    [currentFolderId, handleMoveItems, folderIds]
  );

  // Root level actions
  const rootActions: NestedAction<string>[] = useMemo(() => {
    const actions: NestedAction<string>[] = [];

    // Add "Move to Root" option
    actions.push({
      key: "root",
      label: ROOT_FOLDER_NAME,
      icon: <FolderSimpleIcon size={18} weight="duotone" />,
      run: currentFolderId === null ? undefined : () => handleMoveItems(null),
    });

    // Add all root folders
    const folderActions = buildFolderActions(folderTree);
    actions.push(...folderActions);

    return actions;
  }, [folderTree, currentFolderId, handleMoveItems, buildFolderActions]);

  if (folders.length === 0) {
    return <div className="text-small text-default-500 p-4 text-center">No folders available</div>;
  }

  const noteCount = noteIds.size;
  const folderCount = folderIds.size;

  return (
    <div className="w-full max-w-sm p-2">
      <div className="mb-3">
        <h3 className="text-md font-semibold">Move to Folder</h3>
        <p className="text-small text-default-500">
          {noteCount > 0 && folderCount > 0 ? (
            <>
              Select a destination folder for {noteCount} note{noteCount > 1 ? "s" : ""} and {folderCount} folder
              {folderCount > 1 ? "s" : ""}
            </>
          ) : noteCount > 0 ? (
            <>
              Select a destination folder for {noteCount} note{noteCount > 1 ? "s" : ""}
            </>
          ) : (
            <>
              Select a destination folder for {folderCount} folder{folderCount > 1 ? "s" : ""}
            </>
          )}
        </p>
      </div>

      <NestedContentNavigator
        className="max-h-64"
        actions={rootActions}
        renderAction={(action, onPress) => {
          // For folders with nested actions, they should be navigable even without a run function
          const isCurrent = action.run === undefined && !action.nestedActions;
          const isDisabled = isMoving || isCurrent;

          return (
            <Button
              endContent={
                action.nestedActions ? (
                  <ArrowRightIcon size={16} />
                ) : isCurrent ? (
                  <span className="text-success text-xs">Current</span>
                ) : null
              }
              fullWidth
              isDisabled={isDisabled}
              key={action.key}
              startContent={action.icon}
              variant="light"
              onPress={onPress}
            >
              <span className="flex-1 overflow-x-hidden text-left text-ellipsis">{action.label}</span>
            </Button>
          );
        }}
      />
    </div>
  );
}
